import {
    Create,
    NumberInput,
    ReferenceInput,
    SelectInput,
    SimpleForm,
    TextInput
} from '@/components/admin';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useNotify } from 'ra-core';
import { Lock } from 'lucide-react';

const NumberInputWithLock = ({ source, label, disabled }: { source: string, label: string, disabled?: boolean }) => {
    return (
        <div className="relative">
            <NumberInput 
                source={source} 
                label={label} 
                disabled={disabled} 
                className="w-full"
            />
            {disabled && <Lock className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2" />}
        </div>
    );
};

export const VendorQuoteForm = ({ 
    rfqId, 
    open, 
    onOpenChange,
    onSuccess 
}: { 
    rfqId: any, 
    open: boolean, 
    onOpenChange: (open: boolean) => void,
    onSuccess: () => void 
}) => {
    const notify = useNotify();

    const handleSuccess = () => {
        notify('Vendor quote added successfully', { type: 'success' });
        onSuccess();
        onOpenChange(false);
    };

    const handleError = (error: any) => {
        if (error?.message?.includes('duplicate key value') || error?.message?.includes('vendor_quotes_rfq_id_vendor_id_key')) {
            notify('This vendor already has an existing quote for this RFQ. You cannot add a duplicate.', { type: 'error' });
        } else {
            notify(`Error adding quote: ${error?.message || 'Unknown error'}`, { type: 'error' });
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl bg-white">
                <DialogHeader>
                    <DialogTitle>Enter Vendor Quote</DialogTitle>
                    <DialogDescription>
                        Manually enter the quote details received from the vendor. 
                        <strong> Base Price is immutable and cannot be changed once saved.</strong>
                    </DialogDescription>
                </DialogHeader>

                <Create 
                    resource="vendor_quotes"
                    record={{ rfq_id: rfqId }}
                    mutationOptions={{ onSuccess: handleSuccess, onError: handleError }}
                    className="p-0 space-y-4"
                    redirect={false}
                >
                    <SimpleForm>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
                            <div className="col-span-full border rounded-md p-4">
                                <ReferenceInput source="vendor_id" reference="vendors">
                                    <SelectInput label="Select Vendor" optionText="name" className="w-full" />
                                </ReferenceInput>
                            </div>

                            <div className="space-y-4 p-4 bg-orange-50/50 rounded-lg border border-orange-100 col-span-full">
                                <h4 className="text-sm font-bold text-orange-900 flex items-center gap-2">
                                    <Lock className="w-4 h-4" /> Pricing Information
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <NumberInputWithLock source="base_price" label="Base Price (Immutable)" />
                                    <NumberInputWithLock source="logistics_cost" label="Logistics / Freight Cost" />
                                </div>
                                <p className="text-xs text-orange-600">The base price will be locked at the database level once saved.</p>
                            </div>

                            <TextInput source="lead_time" label="Lead Time (e.g., '14 Days')" className="w-full" />
                            <TextInput source="delivery_terms" label="Delivery Terms (e.g., 'FOB')" className="w-full" />
                            
                            <TextInput 
                                source="terms_notes" 
                                label="Additional Notes / Terms" 
                                multiline 
                                rows={3} 
                                className="w-full col-span-full" 
                            />
                        </div>
                    </SimpleForm>
                </Create>
            </DialogContent>
        </Dialog>
    );
};
